import { useEffect, useRef, useState } from "react";
import { Landmark, Building2, FileText, Shield, ShieldCheck, Lock, Pencil, Calendar, Heart } from "lucide-react";
import { useArticleCount } from "@/hooks/useArticles";
import { useRegulators } from "@/hooks/useRegulators";

/* ---------- Animated count-up helper ---------- */

interface CounterProps {
  value: number;
  /** Optional suffix shown after the count, e.g. "+" */
  suffix?: string;
  /** Animation duration in ms */
  durationMs?: number;
}

/**
 * Pure-CSS-friendly animated counter. Uses IntersectionObserver to start the
 * count when the strip scrolls into view, then drives the number with
 * requestAnimationFrame. No Framer Motion involved — works identically on
 * mobile & desktop and respects prefers-reduced-motion (instantly settles to
 * the final value if motion is reduced).
 */
const Counter = ({ value, suffix = "", durationMs = 1600 }: CounterProps) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setDisplay(value);
      return;
    }

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - t0) / durationMs);
        // easeOutCubic for a satisfying decelerated finish
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(Math.round(value * eased));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, durationMs]);

  return (
    <span ref={ref} className="tabular-nums">
      {display.toLocaleString()}
      {suffix}
    </span>
  );
};

/* ---------- Trust strip ---------- */

const CREDENTIALS = [
  { Icon: ShieldCheck, label: "Independent editorial" },
  { Icon: Lock, label: "GDPR-compliant" },
  { Icon: Pencil, label: "Reader-supported (FTC)" },
  { Icon: Calendar, label: "Updated daily" },
  { Icon: Heart, label: "No paywalls" },
] as const;

const TrustStrip = () => {
  const { data: articleCount } = useArticleCount();
  const { data: regulators } = useRegulators();
  const regulatorCount = regulators?.filter((r) => r.is_active).length ?? 0;

  // 4 stat tiles. Brokers/prop firms remain aspirational tracking numbers
  // (matches existing hero copy); articles & regulators are live from the DB.
  const stats: Array<{ Icon: typeof Landmark; value: number; suffix: string; label: string; tint: string }> = [
    { Icon: Landmark, value: 6000, suffix: "+", label: "Brokers tracked", tint: "text-primary" },
    { Icon: Building2, value: 2500, suffix: "+", label: "Prop firms", tint: "text-secondary" },
    { Icon: FileText, value: articleCount ?? 0, suffix: "", label: "Articles published", tint: "text-wire-gold" },
    { Icon: Shield, value: regulatorCount, suffix: "", label: "Regulators monitored", tint: "text-wire-purple" },
  ];

  return (
    <section
      aria-labelledby="trust-strip-heading"
      className="border-y border-border/40 bg-card/30"
    >
      <div className="container py-10 md:py-12">
        <h2 id="trust-strip-heading" className="sr-only">
          Trusted by traders worldwide
        </h2>

        {/* Stat counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
          {stats.map(({ Icon, value, suffix, label, tint }) => (
            <div key={label} className="text-center">
              <div className="inline-flex items-center justify-center mb-2">
                <Icon className={`h-5 w-5 ${tint}`} aria-hidden="true" />
              </div>
              <div className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                <Counter value={value} suffix={suffix} />
              </div>
              <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="my-8 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

        {/* Credentials band */}
        <div className="flex flex-col items-center gap-4">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Trusted by traders worldwide
          </p>
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {CREDENTIALS.map(({ Icon, label }) => (
              <li
                key={label}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground/90"
              >
                <Icon className="h-4 w-4 text-primary/70" aria-hidden="true" />
                <span className="font-medium">{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default TrustStrip;
